import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, delay, first } from 'rxjs/operators';
import { HttpClient, HttpHeaderResponse } from '@angular/common/http';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class LocalizerService {
  public availablePlatforms = [
    { key: 'android', name: 'android' },
    { key: 'ios', name: 'ios' },
    { key: 'angular', name: 'angular' },
    { key: 'spring', name: 'spring boot' },
    { key: 'rosetta', name: 'rosetta - coming soon', disabled: true }
  ];

  public files = {
    androidDownloadTask: `package localizer

import okhttp3.Request
import okhttp3.Response
import okhttp3.HttpUrl
import okhttp3.OkHttpClient
import okhttp3.ResponseBody
import org.gradle.api.DefaultTask
import org.gradle.api.tasks.TaskAction
import java.io.File
import java.io.IOException
import java.security.SecureRandom
import java.security.cert.X509Certificate
import java.time.Duration
import java.time.Instant
import java.time.LocalDateTime
import java.time.ZonedDateTime
import java.util.zip.ZipInputStream
import javax.net.ssl.SSLContext
import javax.net.ssl.X509TrustManager

const val BUFFER_SIZE = 1024

open class DownloadTask : DefaultTask() {

  init {
      group = "localization"
      description = "Update localization"
  }

  private val config by lazy {
      project.extensions.getByType(LocalizerPluginExtension::class.java)
  }

  private val workDir by lazy {
      File(project.rootDir, ".localizer/data")
  }
  private val versionFile by lazy {
      File(project.rootDir, ".localizer/.version")
  }

  private var lastUpdated: Long = 0L

  @TaskAction
  fun downloadTranslations() {
      validateConfig(config)
              .let { (apiUrl, locUrl, module) ->
                  checkForUpdatedTranslations(apiUrl, locUrl)
                          .checkAndFilterResponse()
                          ?.unzipResponseBodyTo(workDir)
                          ?.copyNewTranslationsToResources(workDir, module)
                          ?.updateVersion()
              }
  }

  private fun Response.checkAndFilterResponse(): ResponseBody? {
      return when {
          code() == java.net.HttpURLConnection.HTTP_OK && body() != null -> body()
          code() == java.net.HttpURLConnection.HTTP_NOT_MODIFIED -> {
              println("Translations are UP-TO-DATE")
              null
          }
          else -> null // TODO Check if up to date
      }
  }

  @Throws(IllegalArgumentException::class)
  private fun validateConfig(config: LocalizerPluginExtension): Triple<String, String, String> {
      println("Validating localization config...")

      val apiUrl = config.apiUrl
          ?: throw IllegalArgumentException("localize.apiUrl not specified!")
      val locUrl = config.localizationUrl
          ?: throw IllegalArgumentException("localize.localizationUrl not specified!")
      val module = config.module
          ?: "app".apply { println("localize.module not specified, fallback to \"app\" module") }
      return Triple(apiUrl, locUrl, module)
  }

  @Throws(IOException::class)
  private fun checkForUpdatedTranslations(apiUrl: String, locUrl: String): Response {
      lastUpdated = versionFile.lastModified()

      if (lastUpdated != 0L) {
          println("Current revision: \${LocalDateTime.ofEpochSecond(Duration.ofMillis(lastUpdated).seconds,
                  0, ZonedDateTime.now().offset)}")
      }

      print("Checking for translations... ")

      val request = Request.Builder()
              .get()
              .url(HttpUrl.get(apiUrl)
                      .newBuilder()
                      .addQueryParameter("platform", "android")
                      .addQueryParameter("url", locUrl)
                      .addQueryParameter("lastModified", lastUpdated.toString())
                      .build())
              .build()

      return try {
          lastUpdated = Instant.now().epochSecond // The moment before the request execution
          val sslContext = SSLContext.getInstance("SSL")
          val trustManager = object : X509TrustManager{
              override fun checkClientTrusted(p0: Array<out X509Certificate>?, p1: String?) {
              }

              override fun checkServerTrusted(p0: Array<out X509Certificate>?, p1: String?) {
              }

              override fun getAcceptedIssuers(): Array<X509Certificate> {
                  return emptyArray()
              }
          }
          sslContext.init(null, arrayOf(trustManager), SecureRandom())
          OkHttpClient.Builder().sslSocketFactory(sslContext.socketFactory,trustManager).hostnameVerifier${
      ' '}{ s, sslSession -> true }.build().newCall(request).execute().apply {
              println("COMPLETE")
          }
      } catch (e: IOException) {
          println("FAILED")
          throw e
      }
  }

  @Throws(IOException::class)
  private fun ResponseBody.unzipResponseBodyTo(targetDir: File) {
      println("Unzipping translations... ")

      try {
          targetDir.deleteRecursively()
          targetDir.mkdir()

          use { body ->
              java.util.zip.ZipInputStream(body.byteStream())
                      .unzipTo(targetDir)
          }

          println("Unzipping translations... COMPLETE")
      } catch (e: IOException) {
          println("Unzipping translations... FAILED")
          println("Error while unzipping translations:")
          throw e
      }
  }

  @Throws(IOException::class)
  private fun ZipInputStream.unzipTo(targetDir: File) = use { zip ->
      var entry = zip.nextEntry

      while (entry != null) {
          File(targetDir, entry.name)
                  .apply {
                      println("Creating: \${toPath()}")
                      parentFile.mkdirs()
                      createNewFile()
                      println("Extracting: \${toPath()}")
                  }
                  .outputStream()
                  .use { zip.copyTo(it, BUFFER_SIZE) }
          entry = zip.nextEntry
      }

      zip.closeEntry()
  }

  @Throws(IOException::class)
  private fun Unit.copyNewTranslationsToResources(workDir: File, module: String) {
      print("Coping translations to $module module's resources... ")

      project.subprojects.find { it.name == module }
              ?.let { File(it.projectDir, "/src/main/res") }
              ?.takeIf { it.isDirectory }
              ?.let { File(workDir, "android").copyRecursively(it, true) }

      println("COMPLETE")
  }

  @Throws(IOException::class)
  private fun Unit.updateVersion() {
      versionFile.apply {
          if (exists()) {
              delete()
          }

          createNewFile()
      }
  }
}`,
    androidLocalizerPlugin: `package localizer

import org.gradle.api.Plugin
import org.gradle.api.Project
import java.io.File

class LocalizerPlugin : Plugin<Project> {

    override fun apply(project: Project) {
        project.extensions.create(LOCALIZER_EXTENSION, LocalizerPluginExtension::class.java)
        project.tasks.create("downloadTranslations", DownloadTask::class.java)

        File(project.rootDir, ".localizer").mkdir()
    }
}`,
    androidLocalizerPluginExtension: `package localizer

const val LOCALIZER_EXTENSION = "localizer"

open class LocalizerPluginExtension {
    var apiUrl: String? = null
    var localizationUrl: String? = null
    var module: String? = null
}`,
    androidBuildGradleBuildSrc: `buildscript {
  ext.kotlin_version = '1.3.21'

  repositories {
      jcenter()
      google()
  }
  dependencies {
      classpath "org.jetbrains.kotlin:kotlin-gradle-plugin:$kotlin_version"
  }
}

repositories {
  jcenter()
  google()
}

apply plugin: 'kotlin'

dependencies {
  implementation "org.jetbrains.kotlin:kotlin-stdlib:$kotlin_version"
  implementation 'com.squareup.okhttp3:okhttp:3.11.0'
}`,
    androidBuildGradleProject: `apply plugin: LocalizerPlugin

localizer {
    apiUrl = "https://localizer.wanari.net/localizer"
    localizationUrl = "https://docs.google.com/spreadsheets/d/totallyRealSpreadsheetIdentifier"
    module = "app"`,
    angularLocalize: `const fs = require('fs-extra');
const request = require('request');
const AdmZip = require('adm-zip');
const server = 'https://localizer.wanari.net/localizer?platform=angular&url=';
const url = process.argv[2];
request(server + url)
  .pipe(fs.createWriteStream('temp.zip'))
  .on('close', () => {
    try {
      const zip = new AdmZip('./temp.zip');
      zip.extractAllTo('./temp/', true);
      fs.move('./temp/angular', './src/assets', { overwrite: true }, () => {
        fs.removeSync('./temp.zip');
        fs.removeSync('./temp');
        console.log('Localized!');
      });
    } catch (error) {
      console.log('The given URL is incorrect.');
    }
  });`,
    iosExample: `SHEET_URL="https://docs.google.com/spreadsheets/d/totallyRealSpreadsheetIdentifier"
LOC_FOLDER="\${SRCROOT}/Path/To/Loc/Files"
LOC_PATH="$LOC_FOLDER/Localizable.strings"

echo "Downloading localized string resources"
curl "https://localizer.wanari.net/localizer?platform=ios&url=$SHEET_URL" --output loc.zip
unzip loc.zip -d loc_files

echo "Overwriting current localization"
mv loc_files/iOS/en.lproj/Localizable.strings $LOC_PATH_EN

echo "Deleting download remnants"
rm -R loc_files
rm loc.zip`,
    springPom: `<profile>
    <id>translations</id>
    <pluginRepositories>
        <pluginRepository>
            <id>sonatype-public-repository</id>
            <url>https://oss.sonatype.org/content/groups/public</url>
            <releases>
                <enabled>true</enabled>
            </releases>
            <snapshots>
                <enabled>false</enabled>
            </snapshots>
        </pluginRepository>
    </pluginRepositories>

    <build>
        <plugins>
            <plugin>
                <groupId>com.googlecode.maven-download-plugin</groupId>
                <artifactId>download-maven-plugin</artifactId>
                <version>1.4.1</version>
                <executions>
                    <execution>
                        <id>download-translations</id>
                        <phase>package</phase>
                        <goals>
                            <goal>wget</goal>
                        </goals>
                        <configuration>
                            <url><![CDATA[http://localizer.wanari.net/localizer?platform=spring&url=$SHEET_URL]]></url>
                            <unpack>true</unpack>
                            <outputDirectory>\${project.basedir}/target/translations/</outputDirectory>
                            <outputFileName>translations-{maven.build.timestamp}.zip</outputFileName>
                            <overwrite>true</overwrite>
                        </configuration>
                    </execution>
                </executions>
            </plugin>
            <plugin>
                <artifactId>maven-resources-plugin</artifactId>
                <version>\${maven-resources-plugin.version}</version>
                <executions>
                    <execution>
                        <id>copy-translations</id>
                        <phase>package</phase>
                        <goals>
                            <goal>copy-resources</goal>
                        </goals>

                        <configuration>
                            <outputDirectory>\${project.basedir}/target/classes/i18n/</outputDirectory>
                            <overwrite>true</overwrite>
                            <resources>
                                <resource>
                                    <directory>\${project.basedir}/target/translations/spring/messages</directory>
                                    <includes>
                                        <include>*.properties</include>
                                    </includes>
                                </resource>
                            </resources>
                        </configuration>
                    </execution>
                </executions>
            </plugin>
        </plugins>
    </build>
</profile>`
  };

  constructor(private http: HttpClient) {}

  public localize(platforms: string, url: string): Observable<any> {
    const options = {
      responseType: 'blob' as 'json'
    };
    return this.http.get(`${environment.localizerUrl}?platform=${platforms}&url=${url}`, options).pipe(
      catchError((error: Response) => {
        throw error;
      })
    );
  }
}
